/**
 * Rich Text Editor Component with Markdown Support
 * Provides formatting toolbar and live preview
 */
import React, { useState, useRef, useCallback } from 'react';
import {
  Bold,
  Italic,
  Heading1,
  Heading2,
  List,
  ListOrdered,
  Link,
  Code,
  Quote,
  Eye,
  Edit,
  Image,
} from 'lucide-react';
import { Button } from './button';
import { Textarea } from './textarea';

/**
 * Markdown to HTML, for this editor's preview AND for read-only views.
 *
 * Exported so a saved note renders exactly as it looked while being written.
 * Two converters would drift, and the one people notice is the one that shows
 * a note differently from the editor that produced it.
 *
 * It ESCAPES `&`, `<` and `>` before anything else, so stored content cannot
 * inject markup — that is what makes it safe to put through
 * dangerouslySetInnerHTML. Nothing below may be reordered above those three.
 */
export const renderMarkdownToHtml = (text: string): string => {

  if (!text) return '';

  let html = text
    // Escape HTML
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Headers
    .replace(/^### (.+)$/gm, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="text-xl font-semibold mt-4 mb-2">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold mt-4 mb-2">$1</h1>')
    // Bold and italic
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Code blocks
    .replace(/```([\s\S]*?)```/g, '<pre class="bg-gray-100 dark:bg-gray-800 p-3 rounded-md my-2 overflow-x-auto"><code>$1</code></pre>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code class="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-sm">$1</code>')
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 hover:underline" target="_blank" rel="noopener">$1</a>')
    // Images
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="max-w-full h-auto rounded my-2" />')
    // Blockquotes
    .replace(/^&gt; (.+)$/gm, '<blockquote class="border-l-4 border-gray-300 pl-4 italic text-gray-600 my-2">$1</blockquote>')
    // Unordered lists
    .replace(/^- (.+)$/gm, '<li class="ml-4">$1</li>')
    // Ordered lists
    .replace(/^\d+\. (.+)$/gm, '<li class="ml-4 list-decimal">$1</li>')
    // Line breaks
    .replace(/\n\n/g, '</p><p class="my-2">')
    .replace(/\n/g, '<br />');

  // Wrap list items
  html = html.replace(/(<li class="ml-4">[\s\S]*?<\/li>)+/g, (match) => {
    if (match.includes('list-decimal')) {
      return `<ol class="list-decimal ml-6 my-2">${match}</ol>`;
    }
    return `<ul class="list-disc ml-6 my-2">${match}</ul>`;
  });

  return `<div class="prose dark:prose-invert max-w-none"><p class="my-2">${html}</p></div>`;
};

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
  className?: string;
}

type FormatAction = 'bold' | 'italic' | 'h1' | 'h2' | 'ul' | 'ol' | 'link' | 'code' | 'quote' | 'image';

const formatButtons: { action: FormatAction; icon: React.ReactNode; label: string; shortcut?: string }[] = [
  { action: 'bold', icon: <Bold className="h-4 w-4" />, label: 'Bold', shortcut: 'Ctrl+B' },
  { action: 'italic', icon: <Italic className="h-4 w-4" />, label: 'Italic', shortcut: 'Ctrl+I' },
  { action: 'h1', icon: <Heading1 className="h-4 w-4" />, label: 'Heading 1' },
  { action: 'h2', icon: <Heading2 className="h-4 w-4" />, label: 'Heading 2' },
  { action: 'ul', icon: <List className="h-4 w-4" />, label: 'Bullet List' },
  { action: 'ol', icon: <ListOrdered className="h-4 w-4" />, label: 'Numbered List' },
  { action: 'link', icon: <Link className="h-4 w-4" />, label: 'Link' },
  { action: 'code', icon: <Code className="h-4 w-4" />, label: 'Code' },
  { action: 'quote', icon: <Quote className="h-4 w-4" />, label: 'Quote' },
  { action: 'image', icon: <Image className="h-4 w-4" />, label: 'Image' },
];

export function RichTextEditor({
  value,
  onChange,
  placeholder = 'Write your content here...',
  minHeight = '300px',
  className = '',
}: RichTextEditorProps) {
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const applyFormat = useCallback((action: FormatAction) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    let newText = '';
    let cursorOffset = 0;

    switch (action) {
      case 'bold':
        newText = `**${selectedText || 'bold text'}**`;
        cursorOffset = selectedText ? 0 : 2;
        break;
      case 'italic':
        newText = `*${selectedText || 'italic text'}*`;
        cursorOffset = selectedText ? 0 : 1;
        break;
      case 'h1':
        newText = `# ${selectedText || 'Heading 1'}`;
        cursorOffset = 2;
        break;
      case 'h2':
        newText = `## ${selectedText || 'Heading 2'}`;
        cursorOffset = 3;
        break;
      case 'ul':
        if (selectedText) {
          newText = selectedText.split('\n').map(line => `- ${line}`).join('\n');
        } else {
          newText = '- List item';
        }
        break;
      case 'ol':
        if (selectedText) {
          newText = selectedText.split('\n').map((line, i) => `${i + 1}. ${line}`).join('\n');
        } else {
          newText = '1. List item';
        }
        break;
      case 'link':
        if (selectedText) {
          newText = `[${selectedText}](url)`;
        } else {
          newText = '[link text](url)';
        }
        break;
      case 'code':
        if (selectedText.includes('\n')) {
          newText = '```\n' + selectedText + '\n```';
        } else {
          newText = '`' + (selectedText || 'code') + '`';
        }
        break;
      case 'quote':
        if (selectedText) {
          newText = selectedText.split('\n').map(line => `> ${line}`).join('\n');
        } else {
          newText = '> Quote';
        }
        break;
      case 'image':
        newText = `![${selectedText || 'alt text'}](image-url)`;
        break;
    }

    const newValue = value.substring(0, start) + newText + value.substring(end);
    onChange(newValue);

    // Restore focus and set cursor position
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + newText.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  }, [value, onChange]);

  const renderMarkdown = renderMarkdownToHtml;

  return (
    <div className={`border rounded-lg overflow-hidden ${className}`}>
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b bg-gray-50 dark:bg-gray-800 px-2 py-1">
        <div className="flex items-center gap-1 flex-wrap">
          {formatButtons.map((btn) => (
            <Button
              key={btn.action}
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => applyFormat(btn.action)}
              className="h-8 w-8 p-0"
              title={`${btn.label}${btn.shortcut ? ` (${btn.shortcut})` : ''}`}
              disabled={mode === 'preview'}
            >
              {btn.icon}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant={mode === 'edit' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setMode('edit')}
            className="h-8 px-3"
          >
            <Edit className="h-4 w-4 mr-1" />
            Edit
          </Button>
          <Button
            type="button"
            variant={mode === 'preview' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setMode('preview')}
            className="h-8 px-3"
          >
            <Eye className="h-4 w-4 mr-1" />
            Preview
          </Button>
        </div>
      </div>

      {/* Editor / Preview */}
      <div style={{ minHeight }}>
        {mode === 'edit' ? (
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="border-0 rounded-none focus:ring-0 resize-none w-full"
            style={{ minHeight, fontFamily: 'monospace' }}
          />
        ) : (
          <div
            className="p-4 overflow-auto bg-white dark:bg-gray-900"
            style={{ minHeight }}
            dangerouslySetInnerHTML={{ __html: renderMarkdown(value) }}
          />
        )}
      </div>

      {/* Help text */}
      <div className="border-t bg-gray-50 dark:bg-gray-800 px-3 py-1.5 text-xs text-gray-500">
        <span>Supports Markdown formatting. Use **bold**, *italic*, # headings, - lists, [links](url), and more.</span>
      </div>
    </div>
  );
}

export default RichTextEditor;
